/**
 * useStaff Hook
 * Custom hook cho Staff operations
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { staffService } from "@/services/staff";
import type {
  Staff,
  StaffListParams,
  VerifyPinPayload,
  VerifyPinResponse,
} from "@/types/staff";

// ============================================
// QUERY KEYS
// ============================================

export const staffQueryKeys = {
  all: ["staff"] as const,
  lists: () => [...staffQueryKeys.all, "list"] as const,
  list: (params: StaffListParams) =>
    [...staffQueryKeys.lists(), params] as const,
  verifyPin: () => [...staffQueryKeys.all, "verify-pin"] as const,
};

// ============================================
// HOOKS
// ============================================

interface UseVerifyPinOptions {
  onSuccess?: (data: VerifyPinResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook để verify PIN của staff
 */
export const useVerifyPin = (options?: UseVerifyPinOptions) => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: VerifyPinPayload) => staffService.verifyPin(payload),
    meta: { skipGlobalError: true }, // Error handled by calling component
    onSuccess: (data) => {
      toast.success(t("staff.pin_verified", { name: data.name }));
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });
};

// ============================================
// LIST HOOKS
// ============================================

/**
 * Hook để lấy danh sách staff
 * Mặc định limit=100 để get full list cho filter dropdown
 */
export const useStaffs = (params: StaffListParams = { limit: 100 }) => {
  return useQuery<Staff[], Error>({
    queryKey: staffQueryKeys.list(params),
    queryFn: () => staffService.getList(params),
  });
};
